<template>
    <v-container>
        <v-row>
            <v-col>
                <v-row>
                    <v-col>
                        <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
                    </v-col>
                    <v-col class="button-group">
                        <v-btn v-if="createByPhoneAvailable" class="add-new" color="primary" @click="goToCreateByPhone"
                            >Ny hviteliste-oppføring (Telefon)
                        </v-btn>
                        <v-btn v-if="createByCountryCodeAvailable" class="add-new" color="primary" @click="goToCreateByCountryCode"
                            >Ny hviteliste-oppføring (Landskode)
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-card>
                            <v-data-table :headers="headers" :items="store.profileWhitelists" :items-per-page="100" :loading="isLoading">
                                <template #item.controls="{ item }">
                                    <div class="controls-container" style="text-align: right">
                                        <v-btn class="delete-link" icon variant="text" @click.prevent="deleteWhitelistItem(item.id)">
                                            <v-icon class="delete-icon" icon="mdi-delete" />
                                        </v-btn>
                                    </div>
                                </template>
                                <template #item.phone="{ item }">
                                    {{ formatters.toFullPhone(item.phone) }}
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { VDataTable } from 'vuetify/components';
import { useProfileWhitelistStore } from './profileWhitelistStore';
import router from '@/plugins/router';
import { ProfileWhitelistRels } from './rels';
import { hasLink } from '@/features/shared/LinkHelper';
import { useFormatters } from '@/features/shared/formatters';
import { useCommonStore } from '@/features/shared/commonStore';
import { PhoneCountryCode } from '@/features/shared/models';
import { ProfileWhitelist } from '@/features/profileWhitelist/profileWhitelistModels';

const store = useProfileWhitelistStore();
const formatters = useFormatters();
const commonStore = useCommonStore();

const breadcrumbs = [
    {
        title: 'Hviteliste'
    }
];

const headers = [
    {
        title: 'Landskode',
        key: 'phoneCountryCode',
        sortable: true,
        width: '300px',
        value: (item: ProfileWhitelist) => mapCountryCodeToName(item.phoneCountryCode)
    },
    {
        title: 'Telefon',
        key: 'phone',
        sortable: true
    },
    {
        title: '',
        key: 'controls',
        sortable: false
    }
];
const isLoading = ref(true);
const phoneCountryCodes = ref<Array<PhoneCountryCode>>([]);
const createByPhoneAvailable = computed(() => hasLink(ProfileWhitelistRels.CreateProfileWhitelistByPhone, store.links));
const createByCountryCodeAvailable = computed(() => hasLink(ProfileWhitelistRels.CreateProfileWhitelistByCountryCode, store.links));

onMounted(async () => {
    phoneCountryCodes.value = await commonStore.getCountryPhoneCodes();
    await store.loadProfileWhitelists();
    isLoading.value = false;
});

function goToCreateByPhone() {
    router.push({ name: ProfileWhitelistRels.CreateProfileWhitelistByPhone });
}

function goToCreateByCountryCode() {
    router.push({ name: ProfileWhitelistRels.CreateProfileWhitelistByCountryCode });
}

async function deleteWhitelistItem(id: string) {
    try {
        await store.deleteProfileWhitelist(id);
        await store.loadProfileWhitelists(); // Refresh the list after deletion
    } catch (error) {
        console.error('Failed to delete whitelist item:', error);
    }
}

function mapCountryCodeToName(phoneCountryCode?: string): string {
    if (!phoneCountryCode) return '';
    const country = phoneCountryCodes.value.find((code: PhoneCountryCode) => code.code === phoneCountryCode);
    return country ? country.name : phoneCountryCode;
}
</script>

<style scoped>
.add-new {
    float: right;
    margin-top: 10px;
}

.button-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.delete-link {
    display: flex;
    align-items: center;
    text-decoration: none;
}

.delete-icon {
    margin-right: 4px;
    font-size: 21px;
}

.controls-container {
    display: flex;
    justify-content: flex-end;
    text-align: right;
    padding-right: 40px;
}
</style>
